import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getNotifications, markAllAsRead, markAsRead, ApiNotification } from '../../api/notificationsService';
import { FilterTabs, NotificationItem, Pagination, EmptyState } from '../../components/shared';
import type { NotificationItemProps } from '../../components/shared/NotificationItem/NotificationItem';
import NotificationsEmptyIcon from '../../assets/notifications-bell.png';

const DESIGN_TOKENS = {
  colors: {
    text: {
      primary: '#181818',
      secondary: '#93989C',
    },
    background: {
      white: '#FFFFFF',
      gray: '#F6F8F8',
    },
    border: {
      light: '#F1F4F4',
      primary: '#67314F',
    },
    primary: '#67314F',
  },
  spacing: {
    xs: '8px',
    sm: '16px',
    md: '24px',
    lg: '40px',
    xl: '80px',
  },
  borderRadius: {
    button: '16px',
  },
  typography: {
    h1: {
      fontSize: '44px',
      lineHeight: '1.15',
      fontWeight: 700,
    },
    body: {
      fontSize: '16px',
      lineHeight: '1.4',
      fontWeight: 400,
    },
    bodyBold: {
      fontSize: '16px',
      lineHeight: '1.5',
      fontWeight: 700,
    },
  },
};

// Utility function to format timestamp
const formatTimestamp = (isoDate: string): string => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${day} ${month}, ${hours}:${minutes}`;
};

// Check if notification is new (within last 24 hours)
const isNewNotification = (timestamp: string): boolean => {
  const notificationDate = new Date(timestamp);
  const now = new Date();
  const hoursDiff = (now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60);
  return hoursDiff < 24;
};

// Helper function to extract value from DynamoDB format
const extractDynamoValue = (value: any): string | number | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (value.S) return value.S;
  if (value.N) return parseFloat(value.N);
  return undefined;
};

// Helper function to extract reviewId from metadata
const extractReviewId = (metadata: Record<string, any> | undefined): string | undefined => {
  if (!metadata) return undefined;
  const reviewId = extractDynamoValue(metadata.reviewId);
  return reviewId ? String(reviewId) : undefined;
};

// Helper function to extract questionId from metadata
const extractQuestionId = (metadata: Record<string, any> | undefined): string | undefined => {
  if (!metadata) return undefined;
  const questionId = extractDynamoValue(metadata.questionId);
  return questionId ? String(questionId) : undefined;
};

// Helper function to extract orderId from metadata
const extractOrderId = (metadata: Record<string, any> | undefined): string | undefined => {
  if (!metadata) return undefined;
  const orderId = extractDynamoValue(metadata.orderId);
  return orderId ? String(orderId) : undefined;
};

// Helper function to extract paymentId from metadata
const extractPaymentId = (metadata: Record<string, any> | undefined): string | undefined => {
  if (!metadata) return undefined;
  const paymentId = extractDynamoValue(metadata.paymentId);
  return paymentId ? String(paymentId) : undefined;
};

// Transform API notification to component format
const transformNotification = (
  apiNotification: ApiNotification, 
  index: number,
  onClick?: () => void
): NotificationItemProps => {
  const baseProps = {
    id: apiNotification.id || String(index + 1),
    timestamp: formatTimestamp(apiNotification.timestamp),
    isNew: isNewNotification(apiNotification.timestamp),
    isUnread: apiNotification.status === 'UNREAD',
    onClick,
  };

  // Map backend notification types to frontend types
  switch (apiNotification.type) {
    case 'ORDER':
      return {
        ...baseProps,
        type: 'order',
        title: apiNotification.title,
        description: apiNotification.message,
      };
    
    case 'QUESTION':
      return {
        ...baseProps,
        type: 'question',
        title: apiNotification.title,
        description: apiNotification.message,
      };
    
    case 'REVIEW':
      // Extract rating from metadata (handle DynamoDB format)
      const ratingValue = extractDynamoValue(apiNotification.metadata?.rating);
      const rating = ratingValue ? (typeof ratingValue === 'number' ? ratingValue : parseFloat(String(ratingValue))) : undefined;
      
      return {
        ...baseProps,
        type: 'review',
        title: apiNotification.title,
        description: apiNotification.message,
        rating,
      };
    
    case 'PAYMENT':
    case 'SYSTEM':
    case 'VENDOR_APPLICATION':
    case 'VENDOR_APPROVAL':
    case 'VENDOR_REJECTION':
    default:
      return {
        ...baseProps,
        type: 'other',
        title: apiNotification.title,
        description: apiNotification.message,
      };
  }
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // API state
  const [notifications, setNotifications] = useState<NotificationItemProps[]>([]);
  const [apiNotifications, setApiNotifications] = useState<ApiNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getNotifications();
        
        if (response.success) {
          setApiNotifications(response.data);
          const transformedNotifications = response.data.map((n, index) => {
            // Create onClick handler based on notification type
            let onClick: (() => void) | undefined;
            
            if (n.type === 'REVIEW') {
              const reviewId = extractReviewId(n.metadata);
              console.log('📋 Review notification metadata:', {
                notificationId: n.id,
                metadata: n.metadata,
                extractedReviewId: reviewId,
              });
              if (reviewId) {
                onClick = () => {
                  console.log('🔗 Navigating to review:', reviewId);
                  navigate(`/reviews?reviewId=${encodeURIComponent(reviewId)}`);
                };
              } else {
                console.warn('⚠️ No reviewId found in notification metadata:', n.metadata);
              }
            } else if (n.type === 'QUESTION') {
              const questionId = extractQuestionId(n.metadata);
              console.log('📋 Question notification metadata:', {
                notificationId: n.id,
                metadata: n.metadata,
                extractedQuestionId: questionId,
              });
              if (questionId) {
                onClick = () => {
                  console.log('🔗 Navigating to question:', questionId);
                  navigate(`/questions?questionId=${encodeURIComponent(questionId)}`);
                };
              } else {
                console.warn('⚠️ No questionId found in notification metadata:', n.metadata);
              }
            } else if (n.type === 'ORDER') {
              const orderId = extractOrderId(n.metadata);
              console.log('📦 Order notification metadata:', {
                notificationId: n.id,
                metadata: n.metadata,
                extractedOrderId: orderId,
              });
              if (orderId) {
                onClick = () => {
                  console.log('🔗 Navigating to order:', orderId);
                  navigate(`/orders/${encodeURIComponent(orderId)}`);
                };
              } else {
                console.warn('⚠️ No orderId found in notification metadata:', n.metadata);
              }
            } else if (n.type === 'PAYMENT') {
              const paymentId = extractPaymentId(n.metadata);
              console.log('💳 Payment notification metadata:', {
                notificationId: n.id,
                metadata: n.metadata,
                extractedPaymentId: paymentId,
              });
              if (paymentId) {
                onClick = () => {
                  console.log('🔗 Navigating to transaction:', paymentId);
                  navigate(`/transactions?paymentId=${encodeURIComponent(paymentId)}`);
                };
              } else {
                console.warn('⚠️ No paymentId found in notification metadata:', n.metadata);
              }
            } else if (n.type === 'SYSTEM' || n.type === 'ALERT') {
              onClick = () => {
                console.log('🔗 Navigating to company profile');
                navigate('/company-profile');
              };
            }
            
            return transformNotification(n, index, onClick);
          });
          setNotifications(transformedNotifications);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
        
        // Handle axios errors with better messages
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please make sure the API server is running on http://localhost:3002');
          } else if (err.response) {
            setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
          } else if (err.request) {
            setError('No response from server. Please check if the API is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [navigate]);

  // Calculate notification counts for unread items per category
  const orderCount = apiNotifications.filter(n => n.type === 'ORDER' && n.status === 'UNREAD').length;
  const reviewCount = apiNotifications.filter(n => n.type === 'REVIEW' && n.status === 'UNREAD').length;
  const questionCount = apiNotifications.filter(n => n.type === 'QUESTION' && n.status === 'UNREAD').length;

  // Calculate unread notifications count
  const unreadCount = apiNotifications.filter(n => n.status === 'UNREAD').length;

  // Filter tabs configuration
  const tabs = [
    { label: 'All', value: 'All', badge: unreadCount > 0 ? unreadCount : undefined },
    { label: 'Orders', value: 'Orders', badge: orderCount > 0 ? orderCount : undefined },
    { label: 'Reviews', value: 'Reviews', badge: reviewCount > 0 ? reviewCount : undefined },
    { label: 'Questions', value: 'Questions', badge: questionCount > 0 ? questionCount : undefined },
    { label: 'Other', value: 'Other' },
  ];

  // Filter notifications based on active tab
  const filteredNotifications = notifications.filter(notification => {
    if (activeTab === 'All') return true;
    if (activeTab === 'Orders') return notification.type === 'order';
    if (activeTab === 'Reviews') return notification.type === 'review';
    if (activeTab === 'Questions') return notification.type === 'question';
    if (activeTab === 'Other') return notification.type === 'other';
    return true;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentNotifications = filteredNotifications.slice(startIndex, endIndex);
  const totalItems = filteredNotifications.length;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  const handleReadAll = async () => {
    try {
      await markAllAsRead();
      // Refetch notifications to update the UI
      const response = await getNotifications();
      if (response.success) {
        setApiNotifications(response.data);
        const transformedNotifications = response.data.map((n, index) => transformNotification(n, index));
        setNotifications(transformedNotifications);
      }
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await markAsRead(notificationId);
      // Update the notification in state without refetching
      setApiNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, status: 'READ' as const }
            : n
        )
      );
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId
            ? { ...n, isUnread: false }
            : n
        )
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: DESIGN_TOKENS.spacing.md,
        padding: DESIGN_TOKENS.spacing.lg,
        backgroundColor: DESIGN_TOKENS.colors.background.white,
        minHeight: '100vh',
        flex: 1,
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          gap: DESIGN_TOKENS.spacing.sm,
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: DESIGN_TOKENS.spacing.xs,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Typography
              sx={{
                fontSize: DESIGN_TOKENS.typography.h1.fontSize,
                lineHeight: DESIGN_TOKENS.typography.h1.lineHeight,
                fontWeight: DESIGN_TOKENS.typography.h1.fontWeight,
                color: DESIGN_TOKENS.colors.text.primary,
              }}
            >
              Notifications
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: DESIGN_TOKENS.typography.body.fontSize,
              lineHeight: DESIGN_TOKENS.typography.body.lineHeight,
              fontWeight: DESIGN_TOKENS.typography.body.fontWeight,
              color: DESIGN_TOKENS.colors.text.primary,
            }}
          >
            See what's happening in your account
          </Typography>
        </Box>

        {/* Mark All As Read Button - Only show when there are unread notifications */}
        {unreadCount > 0 && (
          <Button
            onClick={handleReadAll}
            sx={{
              height: '56px',
              padding: '2px 32px 0px',
              borderRadius: DESIGN_TOKENS.borderRadius.button,
              border: `1px solid ${DESIGN_TOKENS.colors.border.primary}`,
              backgroundColor: 'transparent',
              color: DESIGN_TOKENS.colors.primary,
              fontSize: DESIGN_TOKENS.typography.bodyBold.fontSize,
              lineHeight: DESIGN_TOKENS.typography.bodyBold.lineHeight,
              fontWeight: DESIGN_TOKENS.typography.bodyBold.fontWeight,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: DESIGN_TOKENS.colors.background.gray,
              },
            }}
          >
            Mark All As Read
          </Button>
        )}
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px' 
        }}>
          <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary }} />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '12px',
            fontFamily: 'inherit',
          }}
        >
          {error}
        </Alert>
      )}

      {/* Filter Tabs - Only show if there are notifications */}
      {!loading && !error && notifications.length > 0 && (
        <FilterTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      )}

      {/* Notifications Table */}
      {!loading && !error && currentNotifications.length > 0 ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {currentNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                {...notification}
                {...({ onMarkAsRead: handleMarkAsRead } as Partial<NotificationItemProps>)}
              />
            ))}
          </Box>

          {/* Pagination */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: DESIGN_TOKENS.spacing.md,
            }}
          >
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              onPageChange={handlePageChange}
            />
            <Typography
              sx={{
                fontSize: DESIGN_TOKENS.typography.body.fontSize,
                lineHeight: DESIGN_TOKENS.typography.body.lineHeight,
                fontWeight: DESIGN_TOKENS.typography.body.fontWeight,
                color: DESIGN_TOKENS.colors.text.secondary,
              }}
            >
              {startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems} notifications
            </Typography>
          </Box>
        </>
      ) : !loading && !error ? (
        <EmptyState
          title="No notifications"
          description="No events to show"
          showBackgroundPattern={true}
          icon={
            <img 
              src={NotificationsEmptyIcon} 
              alt="No notifications"
              width="120"
              height="120"
            />
          }
        />
      ) : null}
    </Box>
  );
};

export default Notifications;

